// import React, { StrictMode } from 'react';
import React from 'react';
import { hydrate, render } from 'react-dom';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { createBrowserHistory } from 'history';
import { loadableReady } from '@loadable/component';
import * as Sentry from '@sentry/react';
import SimpleCache from './server/cache/simple-cache';
import { version } from '../package.json';
import App from './app';
import { configureStore } from './store/configureStore';
import ServerContext from './serverContext';
import ScrollToTop from './routes/scroll-to-top';
import { setRunningBuildId } from './helpers/buildId';
import { registerServiceWorker } from './helpers/swUpdate';
import { installChunkReloadHandler, clearChunkReloadGuard } from './helpers/chunkReload';

const initClient = async () => {
  // Recover from a stale-asset chunk failure by reloading onto the current build.
  installChunkReloadHandler();

  const store = await configureStore(window.__PRELOADED_STATE__);
  const { sentryDsn, appEnv, domain, apiUrl } = store.getState().domainConfig;

  const serverData = window.__SERVER_DATA__ || {};
  const isCsrFallback = !!serverData.__CSR_FALLBACK__;
  delete serverData.__CSR_FALLBACK__;
  const cache = new SimpleCache(serverData);

  const buildId = window.__BUILD_ID__ || version;
  setRunningBuildId(buildId);

  console.log(`🚀 v${version} (build ${buildId})${isCsrFallback ? ' (CSR fallback)' : ''}`);
  const history = createBrowserHistory();
  delete window.__PRELOADED_STATE__;
  delete window.__SERVER_DATA__;
  delete window.__BUILD_ID__;

  // Start Mirage mock server if in development and no Cypress
  if (process.env.NODE_ENV === 'development' && !window.Cypress && process.env.RAZZLE_USE_MIRAGE === 'true') {
    const { makeServer } = require('./mirage');
    makeServer({ environment: 'development' });
  }

  // Initialize Sentry on every domain when DSN is present.
  if (sentryDsn) {
    Sentry.init({
      dsn: sentryDsn,
      environment: appEnv,
      release: version,
      integrations: [
        new Sentry.BrowserTracing({
          // See docs for support of different versions of variation of react router
          // https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router/
          routingInstrumentation: Sentry.reactRouterV5Instrumentation(history),
          // Only send sentry-trace headers to our own backend.
          // Prevents CORS issues and trace leakage to Auth0/Stripe/Google.
          tracePropagationTargets: apiUrl ? [apiUrl] : [],
        }),
        new Sentry.Replay(),
      ],
      // Lower sample rate in production to control quota across 68 domains.
      // Full sampling in staging/dev for richer debug data.
      tracesSampleRate: appEnv === 'production' ? 0.1 : 1.0,

      // Capture Replay for 10% of all sessions,
      // plus for 100% of sessions with an error
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      sendDefaultPii: false,
      beforeSend(event) {
        if (event.user) {
          delete event.user.ip_address;
          delete event.user.email;
        }
        if (event.request) {
          delete event.request.cookies;
          delete event.request.data;
          if (event.request.headers) {
            delete event.request.headers['Cookie'];
            delete event.request.headers['Authorization'];
            delete event.request.headers['X-Forwarded-For'];
          }
        }
        return event;
      },
    });

    Sentry.setTag('domain', domain);
    Sentry.setTag('runtime', 'browser');
    Sentry.setTag('app_env', appEnv);
    Sentry.setTag('build_id', buildId);
  }

  const mountApp = isCsrFallback ? render : hydrate;
  loadableReady(() => {
    mountApp(
      // <StrictMode>
      <Provider store={store}>
        <ServerContext.Provider value={cache}>
          <BrowserRouter>
            <ScrollToTop />
            <App />
          </BrowserRouter>
        </ServerContext.Provider>
      </Provider>,
      // </StrictMode>,
      document.getElementById('root')
    );
    // Chunks loaded and the app mounted, so reset the reload guard for any
    // future, unrelated chunk failure.
    clearChunkReloadGuard();
  });

  if (module.hot) {
    module.hot.accept('./app', () => {
      loadableReady(() => {
        hydrate(
          // <StrictMode>
          <Provider store={store}>
            <ServerContext.Provider value={cache}>
              <BrowserRouter>
                <ScrollToTop />
                <App />
              </BrowserRouter>
            </ServerContext.Provider>
          </Provider>,
          // </StrictMode>,
          document.getElementById('root')
        );
      });
    });
  }

  const swRegisteredEvent = new Event('swRegistered');

  // Get the user-agent string
  const userAgentString = navigator.userAgent;

  // Detect Chrome
  const chromeAgent = userAgentString.indexOf('Chrome') > -1;

  // Detect Safari
  let safariAgent = userAgentString.indexOf('Safari') > -1;

  // Discard Safari since it also matches Chrome
  if (chromeAgent && safariAgent) safariAgent = false;

  if ('serviceWorker' in navigator && !safariAgent) {
    window.addEventListener('load', () => {
      registerServiceWorker(() => {
        window.dispatchEvent(new Event('swUpdateReady'));
      }).then((registration) => {
        if (registration) {
          window.dispatchEvent(swRegisteredEvent);
          window.swRegistered = true;
        }
      });
    });
  } else if ('serviceWorker' in navigator && safariAgent) {
    window.addEventListener('load', () => {
      navigator.serviceWorker
        .getRegistrations()
        .then(function (registrations) {
          for (const registration of registrations) {
            registration.unregister();
          }
        })
        .catch(function (err) {
          console.log('Service Worker registration failed: ', err);
        });
    });
  }

  // expose store when run in Cypress
  if (window.Cypress) {
    window.store = store;
  }
};

initClient();
